import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Facebook, Instagram, Send, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", service: "Residential Cleaning", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success(`Thanks, ${form.name.split(" ")[0]}! Your quote request is in — Lauren will be in touch shortly.`);
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong sending your request. Please call or text us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-28 md:py-36" data-testid="contact-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
        <div>
          <Reveal>
            <p className="text-[11px] md:text-xs uppercase tracking-[0.28em] text-seateal font-medium">
              Get a free quote
            </p>
            <h2 className="mt-5 font-display font-light tracking-tighter leading-none text-4xl md:text-6xl text-pine">
              Let's make it{" "}
              <span className="font-editorial italic font-semibold text-seateal">sparkle.</span>
            </h2>
          </Reveal>

          <Reveal delay={0.12}>
            <a
              href="tel:+18654550375"
              className="mt-12 block font-display font-light tracking-tight text-4xl md:text-6xl text-pine hover:text-seateal transition-colors duration-300"
              data-testid="contact-phone-link"
            >
              (865) 455-0375
            </a>
            <p className="mt-3 text-sm font-light text-pine/55">Call or text — the fastest way to reach us.</p>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-10 space-y-4">
              <a
                href="mailto:sparkling.ambitions@icloud.com"
                className="flex items-center gap-3 text-base font-light text-pine/80 hover:text-seateal transition-colors duration-300"
                data-testid="contact-email-link"
              >
                <Mail size={17} strokeWidth={1.75} className="text-seateal" />
                sparkling.ambitions@icloud.com
              </a>
              <p className="flex items-center gap-3 text-base font-light text-pine/80" data-testid="contact-location">
                <MapPin size={17} strokeWidth={1.75} className="text-seateal" />
                Knoxville, TN & surrounding areas
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.28}>
            <div className="mt-10 flex items-center gap-4">
              <a
                href="https://www.facebook.com/share/1DV95SthrX/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Sparkling Ambitions on Facebook"
                className="w-11 h-11 rounded-full border border-pine/15 flex items-center justify-center text-pine hover:bg-foam hover:border-foam transition-colors duration-300"
                data-testid="contact-facebook-link"
              >
                <Facebook size={17} strokeWidth={1.75} />
              </a>
              <a
                href="https://www.instagram.com/sparklingambitionsknox"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Sparkling Ambitions on Instagram"
                className="w-11 h-11 rounded-full border border-pine/15 flex items-center justify-center text-pine hover:bg-foam hover:border-foam transition-colors duration-300"
                data-testid="contact-instagram-link"
              >
                <Instagram size={17} strokeWidth={1.75} />
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            className="bg-white rounded-[2rem] border border-border/70 shadow-[0_20px_70px_rgba(2,44,34,0.07)] p-8 md:p-12"
            data-testid="quote-form"
          >
            <h3 className="font-display font-medium tracking-tight text-2xl text-pine">Request your quote</h3>
            <p className="mt-2 text-sm font-light text-pine/55">
              Tell us about your space — we usually reply the same day.
            </p>

            <div className="mt-9 space-y-7">
              <input
                required
                type="text"
                value={form.name}
                onChange={update("name")}
                placeholder="Your name"
                className="field-input"
                data-testid="quote-form-name-input"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                <input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="Phone"
                  className="field-input"
                  data-testid="quote-form-phone-input"
                />
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="Email"
                  className="field-input"
                  data-testid="quote-form-email-input"
                />
              </div>
              <select
                value={form.service}
                onChange={update("service")}
                className="field-input cursor-pointer"
                data-testid="quote-form-service-select"
              >
                <option>Residential Cleaning</option>
                <option>STR / Airbnb Turnover</option>
                <option>Commercial Cleaning</option>
                <option>Deep Cleaning</option>
                <option>Move-In / Move-Out Cleaning</option>
              </select>
              <textarea
                rows={3}
                value={form.message}
                onChange={update("message")}
                placeholder="Tell us about your space (size, rooms, dates…)"
                className="field-input resize-none"
                data-testid="quote-form-message-input"
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="group mt-10 w-full rounded-full bg-pine text-white py-4 font-display font-medium tracking-wide flex items-center justify-center gap-3 hover:bg-seateal disabled:opacity-60 transition-colors duration-300"
              data-testid="quote-form-submit-button"
            >
              {sending ? (
                <Loader2 size={17} className="animate-spin" />
              ) : (
                <Send size={15} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-1" />
              )}
              {sending ? "Sending…" : "Send Quote Request"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
